import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoRail } from "@/components/site/foto/PhotoRail";
import { coverPhotoFor, photosBy } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  claim,
  faqPage,
  imageGallery,
  serviceSchema,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("foretagsportratt", "portratt");

const PATH = "/foto/foretagsportratt";
const TITLE = "Företagsporträtt och headshots — fotograf | Fenny";
const DESCRIPTION =
  "Företagsporträtt och LinkedIn-headshots med enhetligt ljus och manér. 25–40 personer per dag, leverans inom 3–5 arbetsdagar, offert per uppdrag.";

const FAQ: LandingFAQ[] = [
  {
    q: "Hur många hinner ni fotografera på en dag?",
    a: "Med fast studioljus och ett förberett schema hinner vi 25–40 personer på en dag, ungefär tio minuter per person. Miljöporträtt där vi flyttar mellan platser tar längre tid: räkna med 12–18 personer.",
  },
  {
    q: "Vad ska medarbetarna ha på sig?",
    a: "Enfärgade plagg utan starka mönster fungerar bäst och åldras långsammast. Vi skickar en kort klädguide till alla deltagare innan dagen, tillsammans med schemat.",
  },
  {
    q: "Kan nya medarbetare fotograferas in i samma serie senare?",
    a: "Ja. Vi dokumenterar ljussättning, bakgrund, objektiv och beskärning. Vid kompletteringar återskapas samma uppsättning så att bilderna matchar de befintliga.",
  },
  {
    q: "Vad är skillnaden mellan headshot och porträtt?",
    a: "Ett headshot är en tajt bild från bröstet och upp, gjord för profilbild i LinkedIn eller på sajten. Ett porträtt är friare i utsnitt och miljö och används i pressrum, artiklar och säljmaterial. Vi levererar oftast båda från samma pass.",
  },
  {
    q: "Vad kostar företagsporträtt?",
    a: "Priset offereras per uppdrag och styrs av antal personer, om vi fotograferar i studio eller på plats och hur många färdiga bilder varje person ska få. Ni får ett fast pris innan dagen bokas.",
  },
  {
    q: "Ingår retusch?",
    a: "Ja. Alla levererade bilder får färgsättning och en varsam retusch — vi tar bort tillfälligheter som en lös hårslinga, men behåller ansiktet så som det ser ut.",
  },
];

export const Route = createFileRoute("/foto/foretagsportratt")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Företagsporträtt och headshots — Fenny Foto" },
      {
        property: "og:description",
        content: "Enhetliga porträtt till sajt, LinkedIn och pressrum. Offert per uppdrag.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Hur går företagsporträtt till och vad kostar de?",
            answer:
              "Med fast studioljus och förberett schema fotograferar vi 25–40 företagsporträtt på en arbetsdag, cirka tio minuter per person. Varje person får både headshot och friare porträtt, retuscherade och levererade inom 3–5 arbetsdagar. Ljussättning och beskärning dokumenteras så att nya medarbetare kan fotograferas in i samma serie. Priset offereras per uppdrag. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })),
      },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Företagsporträtt och headshots",
            path: PATH,
            serviceType: "Porträttfotografering",
            description:
              "Företagsporträtt och headshots för webb, LinkedIn och pressrum. Enhetligt bildmanér över tid.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          claim({
            text: "Med fast studioljus och förberett schema fotograferas 25–40 företagsporträtt på en arbetsdag, cirka tio minuter per person.",
            path: PATH,
            about: "Företagsporträtt",
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: "Företagsporträtt — exempel",
            path: PATH,
            description: "Exempel på företagsporträtt fotograferade av Fenny för Fenny.",
            images: photosBy("portratt"),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Företagsporträtt",
              "LinkedIn-headshots",
              "Personalbilder",
              "Fotograf Stockholm",
              "Studiofotografering",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Företagsporträtt", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const portratt = photosBy("portratt");
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Företagsporträtt"
      title={<>Porträtt som ser ut att höra ihop — för att de gör det.</>}
      lede="Enhetliga företagsporträtt och headshots till sajt, LinkedIn och pressrum. Vi bygger studioljus hos er, kör hela organisationen på ett schema och sparar uppsättningen så att nya medarbetare kan fotograferas in i samma serie."
      tldr={
        <p>
          <strong>25–40 porträtt per dag</strong> med fast studioljus, cirka tio
          minuter per person. Leverans av retuscherade bilder inom{" "}
          <strong>3–5 arbetsdagar</strong> i webb- och tryckformat.{" "}
          <strong>Offert per uppdrag</strong> — inga fasta paketpriser.
        </p>
      }
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Företagsfotografering", to: "/foto/foretagsfotografering" }}
      trackingId="foto_foretagsportratt"
      stills={false}
      videos={false}
      afterHero={
        <PhotoRail
          photos={portratt.slice(0, 12)}
          eyebrow="Porträtt"
          heading="Samma ljus, olika människor"
          lede="Klicka på en bild för att se den större."
        />
      }
      bullets={[
        { title: "Tio minuter per person", body: "Schemalagt pass där ingen behöver vänta eller blockera en hel förmiddag." },
        { title: "Dokumenterat manér", body: "Ljus, bakgrund och beskärning sparas — kompletteringar matchar originalserien." },
        { title: "Två utsnitt", body: "Tajt headshot för profiler och friare porträtt för pressrum och artiklar." },
      ]}
      process={{
        heading: "Porträttdagen steg för steg",
        steps: [
          { label: "Schema", body: "Vi delar upp deltagarna i tider och skickar klädguide.", duration: "innan dagen" },
          { label: "Riggning", body: "Studioljus byggs i ett konferensrum. Cirka 3×3 meter räcker.", duration: "45 min" },
          { label: "Fotografering", body: "Två utsnitt per person, direkt visning på skärm.", duration: "10 min/person" },
          { label: "Urval", body: "Ni väljer bild per person, eller så väljer vi åt er.", duration: "1–2 dagar" },
          { label: "Retusch och leverans", body: "Färgsättning, varsam retusch, leverans i två format.", duration: "dag 3–5" },
        ],
      }}
      sections={[
        {
          id: "studio-eller-miljo",
          heading: "Studio eller miljö?",
          body: (
            <>
              <p>
                Studioporträtt med ren bakgrund åldras långsammast och är enklast
                att komplettera i efterhand — därför är det förstahandsvalet när
                hela organisationen ska synas på sajten. Se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/studiofotografering">
                  studiofotografering
                </a>
                .
              </p>
              <p>
                Miljöporträtt i era lokaler säger mer om verksamheten och fungerar
                bättre i artiklar, rekrytering och pressmaterial. Många väljer
                studio för alla och miljö för ledningsgruppen.
              </p>
            </>
          ),
        },
        {
          id: "linkedin",
          heading: "Porträtt för LinkedIn",
          body: (
            <p>
              LinkedIn visar profilbilden som en liten cirkel. Det ställer krav på
              tajt utsnitt, tydlig separation mot bakgrunden och ljus på ögonen.
              Vi levererar därför alltid en kvadratisk variant beskuren för
              profilbild vid sidan av det ordinarie utsnittet.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Dags att uppdatera porträtten?",
        body: "Berätta hur många ni är och var ni finns, så får ni ett fast pris inom en arbetsdag.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={
        <FotoQuoteForm location="foto_foretagsportratt" defaultAssignment="foretagsportratt" />
      }
    />
  );
}
